import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { bulkInviteCandidates, getAllExams, getCandidates, deactivateCandidate } from '../../api/admin.api';
import axiosInstance from '../../api/axiosInstance';

const downloadSample = async () => {
  const res = await axiosInstance.get('/admin/candidates/sample', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample_candidates.xlsx';
  a.click();
  window.URL.revokeObjectURL(url);
};

const LIMIT = 20;

// Sortable column header
const SortTh = ({ label, field, sort, onSort, className = '' }) => {
  const active = sort.field === field;
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 ${className}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-slate-300">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
};

const CandidateManagement = () => {
  // ── Upload state ────────────────────────────────────────────────────────────
  const [file, setFile] = useState(null);
  const [examId, setExamId] = useState('');
  const [exams, setExams] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // ── Candidates list state ───────────────────────────────────────────────────
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [deactivating, setDeactivating] = useState(null);

  // ── Search & sort ───────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const debounceRef = useRef(null);

  // Load exams for dropdown
  useEffect(() => {
    getAllExams().then((res) => setExams(res.data.exams || [])).catch(() => {});
  }, []);

  // Load candidates list
  const loadCandidates = useCallback((p = 1, s = search) => {
    setListLoading(true);
    getCandidates({ page: p, limit: LIMIT, search: s })
      .then((res) => { setCandidates(res.data.rows); setTotal(res.data.total); })
      .finally(() => setListLoading(false));
  }, [search]);

  useEffect(() => { loadCandidates(page, search); }, [page]); // eslint-disable-line

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
      loadCandidates(1, val);
    }, 400);
  };

  const handleSort = (field) => {
    setSort((prev) => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  // Client-side sort on current page
  const sorted = [...candidates].sort((a, b) => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const av = a[sort.field] ?? '';
    const bv = b[sort.field] ?? '';
    if (sort.field === 'created_at') return dir * (new Date(av) - new Date(bv));
    if (typeof av === 'boolean') return dir * (av === bv ? 0 : av ? -1 : 1);
    return dir * String(av).localeCompare(String(bv));
  });

  const handleUpload = async () => {
    if (!file || !examId) return;
    setUploading(true);
    setResult(null);
    try {
      const res = await bulkInviteCandidates(file, examId);
      setResult(res.data);
      loadCandidates(1, search);
      setPage(1);
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const handleDeactivate = async (candidate) => {
    if (!window.confirm(`Deactivate ${candidate.full_name || candidate.email}? They will no longer be able to log in.`)) return;
    setDeactivating(candidate.id);
    try {
      await deactivateCandidate(candidate.id);
      setCandidates((prev) => prev.map((c) => c.id === candidate.id ? { ...c, is_active: false } : c));
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to deactivate');
    } finally {
      setDeactivating(null);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout>
      <div className="max-w-6xl">

        {/* ── Page header ───────────────────────────────────────────────────── */}
        <div className="mb-7">
          <h1 className="text-2xl font-bold text-slate-900">Candidate Management</h1>
          <p className="text-slate-500 text-sm mt-0.5">Bulk-invite candidates by uploading a CSV or Excel file and selecting an exam.</p>
        </div>

        {/* ── Bulk Import section ───────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 mb-8">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Bulk Import & Invite</h2>

          {/* Format guide + Download sample */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-blue-800 mb-2">Required File Format</p>
                <table className="text-xs text-blue-700 w-full mb-2">
                  <thead>
                    <tr className="border-b border-blue-200">
                      <th className="text-left pb-1 font-semibold">Column</th>
                      <th className="text-left pb-1 font-semibold">Required</th>
                      <th className="text-left pb-1 font-semibold">Example</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    <tr>
                      <td className="py-1 font-mono">email</td>
                      <td className="py-1 text-red-600 font-semibold">Yes</td>
                      <td className="py-1">john@example.com</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">full_name</td>
                      <td className="py-1 text-red-600 font-semibold">Yes</td>
                      <td className="py-1">John Doe</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">mobile</td>
                      <td className="py-1 text-red-600 font-semibold">Yes</td>
                      <td className="py-1">+91 98765 43210</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">college_name</td>
                      <td className="py-1 text-slate-500">Optional</td>
                      <td className="py-1">ABC Engineering College</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-blue-600">
                  Candidates will receive an exam access email with the portal link and access code.
                </p>
              </div>
              <button
                onClick={downloadSample}
                className="flex-shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Sample
              </button>
            </div>
          </div>

          {/* File drop zone */}
          <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-2xl cursor-pointer transition-colors mb-4 ${
            file ? 'border-primary-400 bg-primary-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'
          }`}>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => { setFile(e.target.files[0]); setResult(null); }}
            />
            {file ? (
              <>
                <svg className="w-7 h-7 text-primary-500 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium text-primary-600">{file.name}</p>
                <p className="text-xs text-primary-400 mt-0.5">Click to change</p>
              </>
            ) : (
              <>
                <svg className="w-7 h-7 text-slate-400 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <p className="text-sm font-medium text-slate-500">Click to select file</p>
                <p className="text-xs text-slate-400 mt-0.5">Supports .csv, .xlsx, .xls</p>
              </>
            )}
          </label>

          {/* Exam select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Select Exam <span className="text-red-500">*</span></label>
            <select
              value={examId}
              onChange={(e) => setExamId(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
            >
              <option value="">— Select an exam —</option>
              {exams.map((ex) => (
                <option key={ex.id} value={ex.id}>{ex.title}</option>
              ))}
            </select>
            {exams.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No exams available. Please create an exam first.</p>
            )}
          </div>

          <Button onClick={handleUpload} loading={uploading} disabled={!file || !examId}>
            Upload &amp; Invite
          </Button>

          {result && (
            <div className="mt-5 space-y-3">
              {result.success !== false && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-card">
                  <p className="text-sm font-semibold text-slate-800 mb-3">Upload Summary</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-700">{result.invited}</p>
                      <p className="text-xs text-emerald-600 mt-0.5">Invited</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-amber-700">{result.skipped}</p>
                      <p className="text-xs text-amber-600 mt-0.5">Skipped (already assigned)</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">{result.errors?.length || 0}</p>
                      <p className="text-xs text-red-600 mt-0.5">Errors</p>
                    </div>
                  </div>
                </div>
              )}
              {result.errors?.length > 0 && (
                <Alert type="warning">
                  Errors:
                  <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </Alert>
              )}
              {result.message && <Alert type="error">{result.message}</Alert>}
            </div>
          )}
        </div>

        {/* ── Candidates List ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-4">
            <h2 className="text-base font-semibold text-slate-800 shrink-0">
              All Candidates
              {total > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                  {total}
                </span>
              )}
            </h2>
            {/* Search */}
            <div className="relative max-w-xs w-full">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search name, email, college…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {listLoading ? (
            <Spinner className="py-16" />
          ) : sorted.length === 0 ? (
            <p className="text-center text-slate-400 py-16 text-sm">No candidates found.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <SortTh label="Full Name" field="full_name" sort={sort} onSort={handleSort} />
                      <SortTh label="Email" field="email" sort={sort} onSort={handleSort} />
                      <SortTh label="Mobile" field="mobile" sort={sort} onSort={handleSort} />
                      <SortTh label="College" field="college_name" sort={sort} onSort={handleSort} />
                      <SortTh label="Status" field="is_active" sort={sort} onSort={handleSort} />
                      <SortTh label="Registered" field="created_at" sort={sort} onSort={handleSort} />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sorted.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-800">{c.full_name || '—'}</td>
                        <td className="px-4 py-3 text-slate-600">{c.email}</td>
                        <td className="px-4 py-3 text-slate-500">{c.mobile || '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{c.college_name || '—'}</td>
                        <td className="px-4 py-3">
                          {c.is_active ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">Active</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">
                          {new Date(c.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDeactivate(c)}
                            disabled={!c.is_active || deactivating === c.id}
                            className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            {deactivating === c.id ? 'Deactivating…' : 'Deactivate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pages > 1 && (
                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-xs text-slate-400">{total} total candidates</p>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300 transition-colors"
                    >
                      Prev
                    </button>
                    <span className="text-xs text-slate-500">{page} / {pages}</span>
                    <button
                      disabled={page === pages}
                      onClick={() => setPage(page + 1)}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default CandidateManagement;
